import {
  getTaxRateByRegion,
  roundTo2Decimals,
  TaxRate,
} from '@principle-theorem/accounting';
import {
  DestinationEntityRecordStatus,
  IDestinationEntityJobRunOptions,
  IMigratedDataSummary,
  ITranslationMap,
  MissingCustomMappingStatus,
  SkippedDestinationEntityRecord,
  type FailedDestinationEntityRecord,
  type MissingCustomMappingDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISourceEntityRecord,
  type MergeConflictDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  feeToLineItem,
  hasMergeConflicts,
  Invoice,
  Patient,
  stafferToNamedDoc,
  toAccountDetails,
  toINamedDocuments,
  Transaction,
  TransactionOperators,
  TreatmentPlan,
  treatmentToLineItem,
} from '@principle-theorem/principle-core';
import {
  InvoiceStatus,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  type IBasePatient,
  type IBrand,
  type ICustomLineItem,
  type IInvoice,
  type IInvoiceReference,
  type IPatient,
  type IPatientContactDetails,
  type IPractice,
  type IStaffer,
  type ITransaction,
  type ITreatmentLineItem,
  type ITreatmentPlan,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  asDocRef,
  doc,
  Firestore,
  FirestoreMigrate,
  getError,
  INamedDocument,
  ISO_DATE_TIME_FORMAT,
  isSameRef,
  PrimaryKey,
  resolveSequentially,
  runTransaction,
  safeCombineLatest,
  snapshotCombineLatest,
  sortByCreatedAt,
  toNamedDocument,
  toTimestamp,
  WithPrimaryKey,
  type AtLeast,
  type DocumentReference,
  type IIdentifiable,
  type Timestamp,
  type Timezone,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, first, omit, uniq } from 'lodash';
import * as moment from 'moment-timezone';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { ITreatmentPlanWithTreatmentsByPatientId } from '../../../core-practice/destination/entities/patient-invoices';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { InvoiceIdFilter } from '../../../destination/filters/invoice-id-filter';
import { PracticeIdFilter } from '../../../destination/filters/practice-id-filter';
import { resolveMappedCode } from '../../../mappings/item-codes';
import { ItemCodeResourceMapType } from '../../../mappings/item-codes-to-xlsx';
import { PracticeMigration } from '../../../practice-migrations';
import { SourceEntityRecord } from '../../../source/source-entity-record';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  D4WMethodOfPayment,
  D4WPatientInvoice,
  D4WPaymentType,
  PATIENT_INVOICE_RESOURCE_TYPE,
  PatientInvoiceSourceEntity,
  type ID4WPatientInvoicePayment,
} from '../../source/entities/patient-invoice';
import {
  D4WPatientTreatment,
  PatientTreatmentSourceEntity,
  type ID4WPatientTreatment,
} from '../../source/entities/patient-treatment';
import { D4WItemCodeMappingHandler } from '../mappings/item-codes';
import { D4WPracticeMappingHandler } from '../mappings/practices';
import { D4WStafferMappingHandler } from '../mappings/staff';
import { PatientTreatmentPlanDestinationEntity } from './patient-treatment-plans';
import { PatientDestinationEntity } from './patients';
import { PreloadTranslationMap } from '../../../translation-map/preload-translation-map';
import { PATIENT_TRANSACTION_RESOURCE_TYPE } from '../../../destination/entities/patient-invoices';

const MULTI_PATIENT_INVOICE_RESOURCE_TYPE = 'multiPatientInvoicePrimaryPatient';

interface IInvoiceBuildData {
  invoice: IInvoice;
  // accountCreditAmount: number;
  transactions: (IIdentifiable & ITransaction)[];
  planTreatmentPairs: IPlanWithTreatmentPair[];
}

interface IPatientRelationshipData {
  patientId: string;
  patientRef: DocumentReference<IPatient>;
  primaryPatient: WithRef<IPatient>;
}

interface IInvoiceUpdateData {
  invoiceRef: DocumentReference<IInvoice>;
  transactionRefs: DocumentReference<ITransaction>[];
}

interface IInvoiceSuccessData extends IInvoiceUpdateData {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  invoiceRef: DocumentReference<IInvoice>;
  transactionRefs: DocumentReference<ITransaction>[];
}

export const D4W_PATIENT_INVOICE_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_INVOICE_RESOURCE_TYPE,
    label: 'Patient Invoices',
    description: `Each service code will be nested under a "Custom Service Code" treatment on the invoice. Products will be shown as normal.

    If a refund to card needs to be done for a D4W payment, it will need to be performed in D4W, and record a manual refund in Principle.
    Transactions, invoice adjustments, refunds etc... will be done as expected in Principle.

    Some things to note:
    - Non service codes will be added as line items eg: "stock - $60"
    `,
  },
});

export interface ID4WPatientInvoiceJobData {
  sourceInvoice: IGetRecordResponse<D4WPatientInvoice>;
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
  brand: INamedDocument<IBrand>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  practitioners: INamedDocument<IStaffer>[];
  sourceItemCodes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[];
}

interface IPatientInvoiceMigrationData extends IInvoiceBuildData {
  patientRef: DocumentReference<IPatient>;
  invoiceId: string;
  createdAt: Timestamp;
}

interface IPlanWithTreatmentPair
  extends Pick<IInvoiceReference, 'lineItemUid' | 'amount'> {
  planRef: DocumentReference<ITreatmentPlan>;
}

export interface IMultiPatientInvoiceConflictSummary {
  sourcePatientIds: string[];
  sourceInvoiceId: string;
  patientRefs: DocumentReference<IPatient>[];
  status: MissingCustomMappingStatus;
}

export type D4WMissingInvoiceMappingResponse = IDestinationEntityRecord &
  MissingCustomMappingDestinationEntityRecord<IMultiPatientInvoiceConflictSummary>;

export class D4WPatientInvoiceDestinationEntity extends BaseDestinationEntity<
  IInvoiceSuccessData,
  ID4WPatientInvoiceJobData,
  IPatientInvoiceMigrationData,
  D4WMissingInvoiceMappingResponse
> {
  destinationEntity = D4W_PATIENT_INVOICE_DESTINATION_ENTITY;
  override batchLimit = 10000;
  override sorters = [
    {
      key: 'transactions',
      sortByPath: 'reference',
    },
  ];

  override filters = [
    new PracticeIdFilter<ID4WPatientInvoiceJobData>((jobData) =>
      jobData.sourceInvoice.data.practice_id.toString()
    ),
    new InvoiceIdFilter<ID4WPatientInvoiceJobData>((jobData) =>
      this.sourceEntities.invoices
        .getSourceRecordId(jobData.sourceInvoice.data)
        .toString()
    ),
  ];

  override canMigrateByDateRange = true;
  override canMigrateByIdRange = true;

  sourceCountComparison = new PatientInvoiceSourceEntity();

  override sourceEntities = {
    treatments: new PatientTreatmentSourceEntity(),
    invoices: new PatientInvoiceSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    treatmentPlans: new PatientTreatmentPlanDestinationEntity(),
  };

  customMappings = {
    staff: new D4WStafferMappingHandler(),
    practices: new D4WPracticeMappingHandler(),
    itemCodes: new D4WItemCodeMappingHandler(),
  };

  sourceCountDataAccessor(
    data: ID4WPatientInvoiceJobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourceInvoice.record.id;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IInvoiceSuccessData>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return combineLatest([
      Firestore.getDoc(record.data.invoiceRef),
      safeCombineLatest(
        record.data.transactionRefs.map((transactionRef) =>
          Firestore.getDoc(transactionRef)
        )
      ),
    ]).pipe(
      map(([invoiceRef, transactions]) => {
        const data: IMigratedDataSummary[] = [
          {
            label: 'Invoice',
            data: invoiceRef,
          },
        ];

        data.push(
          ...transactions.map((transaction) => ({
            label: 'Transactions',
            data: transaction,
          }))
        );

        return data;
      })
    );
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<ID4WPatientInvoiceJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const practices$ = this.customMappings.practices.getRecords(translationMap);
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const practitioners$ = brand$.pipe(
      switchMap((brand) => all$(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);

    return snapshotCombineLatest([
      brand$,
      practices$,
      staff$,
      practitioners$,
      sourceItemCodes$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.invoices,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourceInvoices,
          [brand, practices, staff, practitioners, sourceItemCodes],
        ]) =>
          sourceInvoices.map((sourceInvoice) => ({
            sourceInvoice,
            brand,
            practices,
            staff,
            practitioners,
            sourceItemCodes,
          }))
      )
    );
  }

  getDestinationEntityRecordUid(data: ID4WPatientInvoiceJobData): string {
    return data.sourceInvoice.record.uid;
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: ID4WPatientInvoiceJobData
  ): Promise<
    | IPatientInvoiceMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & MissingCustomMappingDestinationEntityRecord)
  > {
    try {
      const invoiceId = data.sourceInvoice.data.account_id.toString();
      const practiceId = data.sourceInvoice.data.practice_id.toString();
      const practiceMap = data.practices.find(
        (searchPractice) =>
          searchPractice.sourceIdentifier === practiceId.toString()
      );

      if (!practiceMap?.destinationIdentifier) {
        throw new Error(`Can't find practice with id ${practiceId}`);
      }

      const practice = await Firestore.getDoc(
        practiceMap.destinationIdentifier
      );

      const organisation = await Firestore.getDoc(
        migration.configuration.organisation.ref
      );
      const taxRate = getTaxRateByRegion(organisation.region);

      const treatments = await this.sourceEntities.treatments.filterRecords(
        migration,
        'accountId',
        invoiceId
      );

      const treatmentPlansByPatientId =
        await this._getTreatmentPlansGroupedByPatientId(
          treatments,
          translationMap
        );

      const patientIds = uniq(
        treatments.map((treatment) =>
          String(
            SourceEntityRecord.filterAccessor(treatment.record, 'patientId')
          )
        )
      );

      // Try and resolve the invoice to a single patient
      let invoicePrimaryPatient =
        data.sourceInvoice.data.send_to_patient_id?.toString();

      if (!patientIds.length && invoicePrimaryPatient) {
        patientIds.push(invoicePrimaryPatient);
      }

      if (!patientIds.length) {
        return this._buildErrorResponse(
          data.sourceInvoice,
          `No patients from ids ${patientIds.join(', ')}`
        );
      }

      if (patientIds.length > 1) {
        const invoicePatientRefs = await resolveSequentially(
          patientIds,
          async (patientId) => {
            const patientRef = await translationMap.getDestination<IPatient>(
              patientId,
              PATIENT_RESOURCE_TYPE
            );
            if (!patientRef) {
              throw new Error(`No patient for id ${patientId}`);
            }
            const resolvedPatient = await Firestore.getDoc(patientRef);
            const primaryPatient =
              await Patient.resolvePrimaryContact(resolvedPatient);

            return {
              patientId,
              patientRef,
              primaryPatient: primaryPatient ?? resolvedPatient,
            };
          }
        );

        const allHaveSamePrimaryPatient =
          uniq(
            invoicePatientRefs.map((patient) => patient.primaryPatient.ref.path)
          ).length === 1;

        if (!invoicePrimaryPatient && !allHaveSamePrimaryPatient) {
          const paidByPatientIds = uniq(
            SourceEntityRecord.filterAccessor(
              data.sourceInvoice.record,
              'paidByPatientIds'
            )
          );

          if (paidByPatientIds.length > 1) {
            const sortedPatientIdsKey = [...paidByPatientIds].sort().join(',');
            const storedMapping = await translationMap.getBySource<
              object,
              string
            >(sortedPatientIdsKey, MULTI_PATIENT_INVOICE_RESOURCE_TYPE);

            if (!storedMapping?.destinationValue) {
              return this._buildMissingCustomMappingResponse(
                data.sourceInvoice,
                {
                  sourcePatientIds: paidByPatientIds,
                  sourceInvoiceId: data.sourceInvoice.record.uid,
                  patientRefs: invoicePatientRefs
                    .filter((p) => paidByPatientIds.includes(p.patientId))
                    .map((p) => p.patientRef),
                  status: MissingCustomMappingStatus.Unresolved,
                },
                `Multiple patients found for invoice ${
                  data.sourceInvoice.record.uid
                } — custom mapping required: ${paidByPatientIds.join(', ')}`
              );
            }

            invoicePrimaryPatient = storedMapping.destinationValue.value;
          }

          if (paidByPatientIds.length === 1) {
            invoicePrimaryPatient = paidByPatientIds[0];
          }
        }

        const primaryPatient = invoicePatientRefs[0]?.primaryPatient;
        let patientRef = await resolveMultiPatientInvoiceRef(
          allHaveSamePrimaryPatient,
          translationMap,
          primaryPatient,
          invoicePrimaryPatient
        );
        if (!patientRef) {
          const sortedPatientIdsKey = [...patientIds].sort().join(',');
          const storedMapping = await translationMap.getBySource<
            object,
            string
          >(sortedPatientIdsKey, MULTI_PATIENT_INVOICE_RESOURCE_TYPE);

          if (!storedMapping?.destinationValue) {
            return this._buildMissingCustomMappingResponse(
              data.sourceInvoice,
              {
                sourcePatientIds: patientIds,
                sourceInvoiceId: data.sourceInvoice.record.uid,
                patientRefs: invoicePatientRefs.map((p) => p.patientRef),
                status: MissingCustomMappingStatus.Unresolved,
              },
              `No primary patient resolved for invoice ${
                data.sourceInvoice.record.uid
              } — custom mapping required: ${patientIds.join(', ')}`
            );
          }

          const resolvedPatientRef =
            await translationMap.getDestination<IPatient>(
              storedMapping.destinationValue.value,
              PATIENT_RESOURCE_TYPE
            );

          if (!resolvedPatientRef) {
            throw new Error(
              `Resolved mapping found for invoice ${data.sourceInvoice.record.uid} ` +
                `with patient ID ${storedMapping.destinationValue.value}, ` +
                `but failed to find the patient in the translation map`
            );
          }

          patientRef = resolvedPatientRef;
        }

        const { invoice, transactions, planTreatmentPairs } =
          await this._buildInvoiceData(
            data,
            patientRef,
            invoicePatientRefs,
            practice,
            treatments,
            treatmentPlansByPatientId,
            translationMap,
            migration.configuration.timezone,
            taxRate
          );

        return {
          patientRef,
          createdAt: data.sourceInvoice.record.translations.createdAt,
          invoiceId,
          invoice,
          transactions,
          planTreatmentPairs,
        };
      }

      const patientRef = await translationMap.getDestination<IPatient>(
        patientIds[0],
        PATIENT_RESOURCE_TYPE
      );
      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourceInvoice,
          `No patient for id ${patientIds[0]}`
        );
      }

      const { invoice, transactions, planTreatmentPairs } =
        await this._buildInvoiceData(
          data,
          patientRef,
          [
            {
              patientId: patientIds[0],
              patientRef,
              primaryPatient: await Firestore.getDoc(patientRef),
            },
          ],
          practice,
          treatments,
          treatmentPlansByPatientId,
          translationMap,
          migration.configuration.timezone,
          taxRate
        );

      return {
        patientRef,
        createdAt: data.sourceInvoice.record.translations.createdAt,
        invoiceId,
        invoice,
        transactions,
        planTreatmentPairs,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourceInvoice,
        `Building of migration data failed: ${getError(error)}`
      );
    }
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: IPatientInvoiceMigrationData
  ): Promise<IPatientInvoiceMigrationData | undefined> {
    const invoiceRef = await translationMap.getDestination(
      data.invoiceId,
      PATIENT_INVOICE_RESOURCE_TYPE
    );

    if (!invoiceRef) {
      return;
    }

    try {
      const existingInvoice = await Firestore.getDoc(
        asDocRef<IInvoice>(invoiceRef)
      );

      const invoiceMergeConflict = hasMergeConflicts(
        omit(data.invoice, ['from', 'to']),
        omit(existingInvoice, ['from', 'to']),
        [],
        [
          {
            key: 'transactions',
            sortByPath: 'reference',
          },
        ]
      );

      const existingTransactions: (IIdentifiable & ITransaction)[] = [];

      const transactionMergeConflicts = await resolveSequentially(
        data.transactions,
        async (transaction) => {
          try {
            const existingTransaction = await Firestore.getDoc(
              doc(Invoice.transactionCol(existingInvoice), transaction.uid)
            );

            existingTransactions.push({
              ...existingTransaction,
            });

            return hasMergeConflicts(transaction, existingTransaction);
          } catch {
            return false;
          }
        }
      );

      const hasMergeConflict = [
        invoiceMergeConflict,
        ...transactionMergeConflicts,
      ].some((mergeConflict) => mergeConflict);

      if (hasMergeConflict) {
        return {
          ...data,
          invoice: existingInvoice,
          transactions: existingTransactions,
        };
      }
    } catch {
      return;
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: ID4WPatientInvoiceJobData,
    _migrationData: IPatientInvoiceMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourceInvoice.record.uid,
      label: jobData.sourceInvoice.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourceInvoice.record.id,
    };
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: ID4WPatientInvoiceJobData,
    migrationData: IPatientInvoiceMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const invoiceRef = await this._upsertInvoice(
        migrationData,
        translationMap,
        migrationData.patientRef
      );

      return this._buildSuccessResponse(jobData.sourceInvoice, invoiceRef);
    } catch (error) {
      return this._buildErrorResponse(
        jobData.sourceInvoice,
        `Run job failed: ${getError(error)}`
      );
    }
  }

  buildSkippedResponse(
    data: ID4WPatientInvoiceJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourceInvoice.record.uid,
      label: data.sourceInvoice.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourceInvoice.record.id,
    };
  }

  private _buildSuccessResponse(
    invoice: IGetRecordResponse<D4WPatientInvoice>,
    updateData?: IInvoiceUpdateData
  ): IDestinationEntityRecord {
    return {
      uid: invoice.record.uid,
      label: invoice.record.label,
      data: {
        sourceRef: invoice.record.id,
        ...updateData,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: invoice.record.id,
      migratedAt: toTimestamp(),
    };
  }

  private async _upsertInvoice(
    migrationData: IPatientInvoiceMigrationData,
    translationMap: TranslationMapHandler,
    patientRef: DocumentReference<IPatient>
  ): Promise<IInvoiceUpdateData> {
    return runTransaction(async (firestoreTransaction) => {
      const invoiceDestinationRef = await PreloadTranslationMap.preload(
        translationMap,
        PATIENT_INVOICE_RESOURCE_TYPE,
        Patient.invoiceCol({
          ref: patientRef,
        }),
        migrationData.invoiceId
      );

      const invoiceRef = await FirestoreMigrate.upsertDoc(
        Patient.invoiceCol({
          ref: patientRef,
        }),
        { ...migrationData.invoice, createdAt: migrationData.createdAt },
        invoiceDestinationRef,
        firestoreTransaction
      );

      const transactionRefs = await resolveSequentially(
        migrationData.transactions,
        async (transaction) => {
          const transactionDestinationRef = await PreloadTranslationMap.preload(
            translationMap,
            PATIENT_TRANSACTION_RESOURCE_TYPE,
            Invoice.transactionCol({
              ref: invoiceRef,
            }),
            transaction.uid
          );

          return FirestoreMigrate.upsertDoc(
            Invoice.transactionCol({
              ref: invoiceRef,
            }),
            transaction,
            transactionDestinationRef,
            firestoreTransaction
          );
        }
      );

      // TODO: CU-219c758 - Do we want to set invoice id on the treatment?
      // await resolveSequentially(
      //   migrationData.planTreatmentPairs,
      //   async (planTreatmentPair) => {
      //     const step = await snapshot(
      //       TreatmentPlan.stepForTreatmentById$(
      //         { ref: planTreatmentPair.planRef },
      //         planTreatmentPair.lineItemUid
      //       )
      //     );

      //     if (!step) {
      //       throw new Error(
      //         `No step found for plan ${planTreatmentPair.planRef.id} and treatment ${planTreatmentPair.lineItemUid}`
      //       );
      //     }

      //     await FirestoreMigrate.patchDoc(step.ref, {
      //       treatments: step.treatments.map((treatment) => {
      //         if (treatment.uuid !== planTreatmentPair.lineItemUid) {
      //           return treatment;
      //         }
      //         return {
      //           ...treatment,
      //           invoices: uniqBy(
      //             [
      //               ...treatment.invoices,
      //               {
      //                 invoiceRef,
      //                 lineItemUid: planTreatmentPair.lineItemUid,
      //                 amount: planTreatmentPair.amount,
      //               },
      //             ],
      //             (item) => item.invoiceRef.path
      //           ),
      //         };
      //       }),
      //     });
      //   }
      // );

      return {
        invoiceRef,
        transactionRefs,
      };
    });
  }

  private async _buildInvoiceData(
    data: ID4WPatientInvoiceJobData,
    accountPatientRef: DocumentReference<IPatient>,
    invoicePatientRefs: IPatientRelationshipData[],
    practice: WithRef<IPractice>,
    treatments: IGetRecordResponse<D4WPatientTreatment>[],
    treatmentPlansByPatientId: ITreatmentPlanWithTreatmentsByPatientId[],
    translationMap: TranslationMapHandler,
    timezone: Timezone,
    taxRate: TaxRate
  ): Promise<IInvoiceBuildData> {
    const resolvedPatient = await Firestore.getDoc(accountPatientRef);
    const primaryContact = await Patient.resolvePrimaryContact(resolvedPatient);
    const onBehalfOf = toAccountDetails(
      resolvedPatient as IBasePatient & IPatientContactDetails
    );

    const invoiceTreatments = treatments
      .filter((procedure) =>
        procedure.data.account_id
          ? // TODO: Had two treatments that didn't have a date. Do we need the date filter?
            // procedure.data.date && procedure.data.account_id
            true
          : false
      )
      .map((procedure) => procedure.data);

    const [treatmentLineItems, planTreatmentPairs] = await getTreatments(
      data,
      treatmentPlansByPatientId,
      invoicePatientRefs,
      invoiceTreatments,
      translationMap,
      taxRate
    );

    const miscLineItems = getMiscLineItems(data, invoiceTreatments);

    const invoice = Invoice.init({
      from: toAccountDetails(practice),
      to: primaryContact
        ? {
            ...toAccountDetails(primaryContact),
            onBehalfOf,
          }
        : onBehalfOf,
      practice: toNamedDocument(practice),
      due: data.sourceInvoice.record.translations.dueAt,
      items: [...treatmentLineItems, ...miscLineItems],
      claims: [],
    });
    invoice.reference = data.sourceInvoice.data.account_id.toString();

    const transactions = [
      ...getTransactions(
        data.sourceInvoice.data.payments,
        data.sourceInvoice.record.translations.createdAt,
        timezone,
        invoice.practice.ref
      ),
    ];

    const status = getInvoiceStatus(Invoice.balance(invoice, transactions));
    const lastTransaction = new TransactionOperators(transactions)
      .completed()
      .sort(sortByCreatedAt)
      .reverse()
      .last();

    if (status === InvoiceStatus.Paid && lastTransaction) {
      invoice.paidAt = lastTransaction.createdAt;
    }

    const issuedAt =
      lastTransaction?.createdAt ??
      data.sourceInvoice.record.translations.createdAt;

    if (status !== InvoiceStatus.Draft) {
      invoice.issuedAt = issuedAt;
    }

    Invoice.updateStatus(invoice, status, issuedAt);

    return {
      invoice,
      transactions,
      planTreatmentPairs,
    };
  }

  private _buildErrorResponse(
    invoice: IGetRecordResponse<D4WPatientInvoice>,
    errorMessage?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: invoice.record.uid,
      label: invoice.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: invoice.record.id,
      errorMessage: errorMessage ?? 'Missing required properties for invoice',
      failData: {
        invoiceRef: invoice.record.id,
      },
    };
  }

  private _buildMissingCustomMappingResponse(
    invoice: IGetRecordResponse<D4WPatientInvoice>,
    failData: IMultiPatientInvoiceConflictSummary,
    errorMessage: string
  ): IDestinationEntityRecord & MissingCustomMappingDestinationEntityRecord {
    return {
      uid: invoice.record.uid,
      label: invoice.record.label,
      status: DestinationEntityRecordStatus.MissingCustomMapping,
      sourceRef: invoice.record.id,
      errorMessage,
      failData,
    };
  }

  private async _getTreatmentPlansGroupedByPatientId(
    sourceLineItems: IGetRecordResponse<D4WPatientTreatment>[],
    translationMap: TranslationMapHandler
  ): Promise<ITreatmentPlanWithTreatmentsByPatientId[]> {
    return resolveSequentially(
      uniq(sourceLineItems.map((lineItem) => lineItem.data.patient_id)),
      async (patientId) => {
        const patientRef = await translationMap.getDestination<IPatient>(
          patientId.toString(),
          PATIENT_RESOURCE_TYPE
        );

        if (!patientRef) {
          throw new Error(`No patient for id ${patientId}`);
        }

        const plans = await Firestore.getDocs(
          TreatmentPlan.col({ ref: patientRef })
        );

        const plansWithTreatments = await resolveSequentially(
          plans,
          async (plan) => {
            const allTreatments = await TreatmentPlan.allTreatments(plan);
            return {
              plan,
              allTreatments,
            };
          }
        );

        return {
          patientId,
          plans: plansWithTreatments,
        };
      }
    );
  }
}

async function getTreatments(
  data: ID4WPatientInvoiceJobData,
  treatmentPlans: ITreatmentPlanWithTreatmentsByPatientId[],
  invoicePatientRefs: IPatientRelationshipData[],
  treatments: ID4WPatientTreatment[],
  translationMap: TranslationMapHandler,
  taxRate: TaxRate
): Promise<[ITreatmentLineItem[], IPlanWithTreatmentPair[]]> {
  const planTreatmentPairs: IPlanWithTreatmentPair[] = [];

  const lineItems = compact(
    await resolveSequentially(treatments, async (treatment) => {
      const procedureCode = treatment.item_code;
      const code = resolveMappedCode(
        data.sourceItemCodes,
        treatment.item_id.toString(),
        procedureCode
      );

      if (!code) {
        return;
      }

      const patientId = treatment.patient_id.toString();
      const patientRef = await translationMap.getDestination<IPatient>(
        patientId,
        PATIENT_RESOURCE_TYPE
      );
      if (!patientRef) {
        throw new Error(`No patient for id ${patientId}`);
      }

      const invoicePatientRef = invoicePatientRefs.find(
        (patient) => patient.patientId === patientId
      )?.patientRef;
      if (!invoicePatientRef) {
        throw new Error(`No patient for id ${patientId}`);
      }

      const filteredTreatmentPlans =
        treatmentPlans.find((plan) => plan.patientId.toString() === patientId)
          ?.plans ?? [];

      const treatmentUid = treatment.id.toString();

      const existingTreatment = first(
        compact(
          filteredTreatmentPlans.map((treatmentPlan) => {
            const foundTreatment = TreatmentPlan.findTreatmentInAllTreatments(
              treatmentPlan.allTreatments,
              treatmentUid
            );
            if (!foundTreatment) {
              return;
            }

            return {
              treatment: foundTreatment,
              treatmentPlan: treatmentPlan.plan,
            };
          })
        )
      );

      if (!existingTreatment?.treatment) {
        throw new Error(`No treatment found for id ${treatment.id}`);
      }

      const providerId = treatment.provider_id.toString();
      const practitionerMap = data.staff.find(
        (staffer) => staffer.sourceIdentifier === providerId
      )?.destinationIdentifier;

      if (!practitionerMap) {
        throw new Error(`Couldn't resolve practitioner ${providerId ?? ''}`);
      }
      const practitioner = data.practitioners.find((searchPractitioner) =>
        isSameRef(searchPractitioner.ref, practitionerMap)
      );

      if (!practitioner) {
        throw new Error(`Couldn't resolve practitioner ${providerId ?? ''}`);
      }

      const lineItem = {
        patientRef: invoicePatientRef,
        ...treatmentToLineItem(
          existingTreatment.treatment,
          existingTreatment.treatmentPlan,
          stafferToNamedDoc(practitioner),
          taxRate
        ),
      };

      planTreatmentPairs.push({
        planRef: existingTreatment.treatmentPlan.ref,
        lineItemUid: treatmentUid,
        amount: roundTo2Decimals(lineItem.amount),
      });

      return lineItem;
    })
  );

  return [lineItems, planTreatmentPairs];
}

function getMiscLineItems(
  data: ID4WPatientInvoiceJobData,
  treatments: ID4WPatientTreatment[]
): ICustomLineItem[] {
  return treatments
    .filter((treatment) => !!treatment.account_id)
    .filter((treatment) => {
      const procedureCode = treatment.item_code;
      const code = resolveMappedCode(
        data.sourceItemCodes,
        treatment.item_id.toString(),
        procedureCode
      );

      if (code) {
        return false;
      }

      return true;
    })
    .map((treatment) => {
      const description = treatment.item_code_description
        ? `${treatment.item_code} - ${treatment.item_code_description}`
        : treatment.item_code;
      const cost = roundTo2Decimals(parseFloat(treatment.fee));
      return {
        ...feeToLineItem(description, cost),
        quantity: treatment.times,
      };
    });
}

async function resolveMultiPatientInvoiceRef(
  allHaveSamePrimaryPatient: boolean,
  translationMap: TranslationMapHandler,
  primaryPatient?: WithRef<IPatient>,
  invoicePrimaryPatient?: string
): Promise<DocumentReference<IPatient> | undefined> {
  if (invoicePrimaryPatient) {
    return translationMap.getDestination<IPatient>(
      invoicePrimaryPatient,
      PATIENT_RESOURCE_TYPE
    );
  }

  if (allHaveSamePrimaryPatient && primaryPatient) {
    return primaryPatient.ref;
  }

  return undefined;
}

function getInvoiceStatus(amountRemaining: number): InvoiceStatus {
  if (amountRemaining === 0) {
    return InvoiceStatus.Paid;
  }

  if (amountRemaining > 0) {
    return InvoiceStatus.Issued;
  }

  if (amountRemaining < 0) {
    return InvoiceStatus.Paid;
  }

  return InvoiceStatus.Draft;
}

function getTransactions(
  payments: ID4WPatientInvoicePayment[],
  invoiceCreatedAt: Timestamp,
  timezone: Timezone,
  practiceRef: DocumentReference<IPractice>
): (IIdentifiable & ITransaction)[] {
  return payments.map((payment) => {
    const amount = roundTo2Decimals(
      parseFloat(payment.true_payment_amount ?? '0')
    );

    const isOutgoingPaymentType = [
      D4WPaymentType.RefundFromDeposit,
      D4WPaymentType.RefundOfTreat,
    ].includes(payment.payment_type ?? D4WPaymentType.Usual);

    const type =
      amount < 0 || isOutgoingPaymentType
        ? TransactionType.Outgoing
        : TransactionType.Incoming;

    const uid = compact([payment.payment_plan_id, payment.payment_id]).join(
      '-'
    );

    const reference = `${
      payment.payment_method ?? TransactionProvider.Manual
    }:${uid || uuid()}`;

    const createdAt = payment.tot_payment_created_at
      ? toTimestamp(
          moment.tz(
            payment.tot_payment_created_at,
            ISO_DATE_TIME_FORMAT,
            timezone
          )
        )
      : invoiceCreatedAt;

    const isDiscount =
      payment.payment_method_id === D4WMethodOfPayment.Discount;
    const isDeposit = payment.payment_method_id === D4WMethodOfPayment.Deposit;

    const provider = isDiscount
      ? TransactionProvider.Discount
      : isDeposit
        ? TransactionProvider.AccountCredit
        : TransactionProvider.Manual;

    const transaction: AtLeast<
      ITransaction,
      'reference' | 'from' | 'to' | 'practiceRef'
    > = {
      reference,
      type,
      status: TransactionStatus.Complete,
      from: '',
      to: '',
      amount: Math.abs(amount),
      extendedData: payment,
      createdAt,
      practiceRef,
    };

    return {
      ...Transaction.init({
        ...Transaction.internalReference(provider, uid),
        ...transaction,
      }),
    };
  });
}
